"use client";

import { useEffect } from "react";

import { AppLink } from "@/components/app-link";

import { notFoundRedirectTarget } from "./notFoundRedirect";

/**
 * Global 404 handler. Unknown paths are bounced home, but the redirect is
 * guarded (see `notFoundRedirect.ts`) so a root-like path that resolves to
 * not-found renders this recovery card instead of looping
 * `not-found -> redirect("/") -> not-found -> ...` until the stack overflows
 * (Sentry CUSTOMER-UI-3T). Hardcoded English: this is an error safety-net that
 * must not depend on next-intl resolving for the (possibly invalid) locale.
 *
 * The card always renders; on a redirectable path the imperative replace below
 * navigates away before it matters, so there is no setState-in-effect churn.
 */
export default function NotFound() {
  useEffect(() => {
    const target = notFoundRedirectTarget(window.location.pathname);
    if (target) window.location.replace(target);
  }, []);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-xl font-semibold">Page not found</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        The page you’re looking for doesn’t exist or has moved.
      </p>
      <AppLink
        href="/"
        className="rounded-md bg-zinc-900 px-5 py-2 text-sm font-medium text-white"
      >
        Go to home
      </AppLink>
    </div>
  );
}
